import {walkPointcloud} from "../PointCloudQuery/FullPointExport.js";
import {ALGORITHM_VERSION} from "./cutFillAlgorithm.js";
import {JOB_DEFAULTS, planLevel} from "./cutFillPlan.js";

import {t, getLanguage} from "../../digitization/i18n/index.js";
export {JOB_DEFAULTS, planLevel};

const CHUNK = 1 << 20;

// Automatisk graverapport (udgravning/påfyldning) for en punktsky - også en, der aldrig har været vist: punkterne hentes direkte
// fra oktræet med samme motor som LAS-eksporten (rå range-requests, ingen viewer-state/LRU - rører ikke det, der er indlæst til
// visning), men KUN de grove niveauer, der er nok til en højdemodel med 0,3 m celler. Selve beregningen kører i en worker.
//
//   const {result, plan} = await Potree.computeCutFillVolume(pointcloud, {onProgress, shouldCancel});
//
// `pointcloud` kan være en indlæst punktsky eller en, der er oprettet med Potree.loadPointCloud(url, name, cb) uden at blive
// føjet til scenen (så "ikke set før"). Resultatet har samme form som cut_fill_volume.json (se cutFillAlgorithm.js) plus `meta`
// og `plan`; en senere jobmanager kan skrive samme fil.

// en indekseret oktræ-node som planLevel() forventer den (areal i planen, om den er et blad)
function describeNode(node){
	const box = node.boundingBox;
	return {
		level: node.level || 0,
		numPoints: node.numPoints || 0,
		area: box ? (box.max.x - box.min.x) * (box.max.y - box.min.y) : 0,
		leaf: !Object.values(node.children || {}).some(Boolean),
	};
}

class PointCollector{
	constructor(){
		this.count = 0; // walkPointcloud læser accumulator.count til fremdriften
		this.chunks = [];
		this.fill = CHUNK;
		this.current = null;
		this.sawClassification = false;
		this.sawColor = false;
	}

	next(){
		this.current = {
			x: new Float32Array(CHUNK), y: new Float32Array(CHUNK), z: new Float32Array(CHUNK),
			cls: new Uint8Array(CHUNK), rgb: new Uint8Array(CHUNK * 3),
		};
		this.chunks.push(this.current);
		this.fill = 0;
	}

	add(position, rgba, matrix, classification){
		const w = matrix.elements;
		const n = position.length / 3;
		if(classification) this.sawClassification = true;
		if(rgba) this.sawColor = true;
		for(let i = 0; i < n; i++){
			if(this.fill >= CHUNK) this.next();
			const px = position[i * 3], py = position[i * 3 + 1], pz = position[i * 3 + 2];
			const c = this.current, k = this.fill++;
			c.x[k] = w[0] * px + w[4] * py + w[8] * pz + w[12];
			c.y[k] = w[1] * px + w[5] * py + w[9] * pz + w[13];
			c.z[k] = w[2] * px + w[6] * py + w[10] * pz + w[14];
			if(classification) c.cls[k] = classification[i];
			if(rgba){
				c.rgb[k * 3] = rgba[i * 4]; c.rgb[k * 3 + 1] = rgba[i * 4 + 1]; c.rgb[k * 3 + 2] = rgba[i * 4 + 2];
			}else{
				c.rgb[k * 3] = c.rgb[k * 3 + 1] = c.rgb[k * 3 + 2] = 160;
			}
			this.count++;
		}
	}

	// ét sammenhængende sæt typed arrays (overføres til workeren)
	finish(){
		const n = this.count;
		const x = new Float32Array(n), y = new Float32Array(n), z = new Float32Array(n);
		const cls = new Uint8Array(n), rgb = new Uint8Array(n * 3);
		let offset = 0;
		this.chunks.forEach((c, index) => {
			const m = index === this.chunks.length - 1 ? this.fill : CHUNK;
			x.set(c.x.subarray(0, m), offset); y.set(c.y.subarray(0, m), offset); z.set(c.z.subarray(0, m), offset);
			cls.set(c.cls.subarray(0, m), offset); rgb.set(c.rgb.subarray(0, m * 3), offset * 3);
			offset += m;
		});
		this.chunks = [];
		return {x, y, z, cls: this.sawClassification ? cls : null, rgb: this.sawColor ? rgb : null, count: n};
	}
}

function runWorker(points, params, onStage, shouldCancel){
	return new Promise((resolve, reject) => {
		const worker = new Worker(Potree.scriptPath + "/workers/cutFillWorker.js", {type: "module"});
		let done = false;
		const timer = setInterval(() => {
			if(shouldCancel && shouldCancel() && !done){
				done = true;
				clearInterval(timer);
				worker.terminate();
				reject(new Error(t("Annulleret")));
			}
		}, 200);
		const finish = () => { done = true; clearInterval(timer); worker.terminate(); };

		worker.onmessage = (e) => {
			if(done) return;
			const msg = e.data;
			if(msg.type === "progress"){ onStage(msg.stage); return; }
			finish();
			if(msg.type === "result") resolve(msg.result); else reject(new Error(msg.message));
		};
		worker.onerror = (e) => {
			if(done) return;
			finish();
			reject(new Error(t("Beregningen fejlede: ") + (e.message || t("ukendt fejl i workeren"))));
		};

		const transfer = [points.x.buffer, points.y.buffer, points.z.buffer];
		if(points.cls) transfer.push(points.cls.buffer);
		if(points.rgb) transfer.push(points.rgb.buffer);
		worker.postMessage({type: "compute", points, params, language: getLanguage()}, transfer);
	});
}

// opts: {cell, minDepth, maxPoints, onProgress({phase, nodesVisited, nodesQueued, points} | {phase: "compute", stage}), shouldCancel()}
export async function computeCutFillVolume(pointcloud, opts = {}){
	const o = Object.assign({}, JOB_DEFAULTS, opts);
	const geometry = pointcloud.pcoGeometry;
	if(!geometry || !geometry.root){
		throw new Error(t("Punktskyen er ikke klar (ingen oktræ-hierarki)."));
	}

	const collector = new PointCollector();
	let plan = null;
	const spacing = geometry.spacing;

	await walkPointcloud(pointcloud, {
		nodeFilter: () => true,
		maxLevel: o.maxLevel,
		localFrame: true,
		planNodes: (nodes) => {
			plan = planLevel(nodes.map(describeNode), o);
			return nodes.filter(node => (node.level || 0) <= plan.level);
		},
		extractNode: (position, rgba, matrix, acc, classification) => collector.add(position, rgba, matrix, classification),
		accumulator: collector,
		onProgress: o.onProgress,
		shouldCancel: o.shouldCancel,
		concurrency: 32,
	});

	if(collector.count === 0){
		throw new Error(t("Der blev ikke hentet nogen punkter fra punktskyen."));
	}

	const points = collector.finish();
	const onStage = (stage) => { if(o.onProgress) o.onProgress({phase: "compute", stage}); };
	if(o.onProgress) o.onProgress({phase: "compute", stage: t("Starter beregning ...")});

	const result = await runWorker(points, {
		cell: plan.cell,
		minDepth: o.minDepth,
		minPoints: o.minPoints,
		minGroundPoints: o.minGroundPoints,
	}, onStage, o.shouldCancel);

	const offset = geometry.offset ? [geometry.offset.x, geometry.offset.y, geometry.offset.z] : [0, 0, 0];
	result.plan = {
		algorithmVersion: ALGORITHM_VERSION,
		level: plan.level,
		density: plan.density,
		reachedTarget: plan.reachedTarget,
		lowDensity: plan.lowDensity,
		rootSpacing: spacing,
		levelPoints: plan.points,
		deepestIndexed: plan.deepestIndexed,
		budgetLimited: plan.budgetLimited,
		maxPoints: o.maxPoints,
		offset,
	};
	return result;
}
